import { useQuery } from '@tanstack/react-query'
import { usePlatform } from '@/hooks/usePlatform'
import { useAuth } from '@/hooks/useAuth'
import { fetchTrendingTopics } from '@/services/trendingService'
import { fetchMastodonTrendingTopics } from '@/services/mastodonTrendingService'
import type { TrendingTopic } from '@/services/trendingService'

export type { TrendingTopic }

const TRENDING_LIMIT = 10

export function useTrendingTopics() {
    const { selectedProtocol } = usePlatform()
    const { mastodonSession, pixelfedSession } = useAuth()

    const isAtProto = selectedProtocol === 'bluesky'
    const isMastodon = selectedProtocol === 'mastodon'
    const isPixelfed = selectedProtocol === 'pixelfed'
    const isActivityPub = isMastodon || isPixelfed

    // Resolve the ActivityPub session for the active platform
    const apSession = isMastodon ? mastodonSession : isPixelfed ? pixelfedSession : null

    const atEnabled = isAtProto
    const apEnabled = isActivityPub && apSession !== null
    const enabled = atEnabled || apEnabled

    const query = useQuery<TrendingTopic[]>({
        queryKey:
            isActivityPub && apSession
                ? ['trendingTopics', 'ap', apSession.instance_url]
                : ['trendingTopics'],
        queryFn: () => {
            if (isActivityPub && apSession) {
                return fetchMastodonTrendingTopics(
                    TRENDING_LIMIT,
                    apSession.instance_url,
                    apSession.access_token
                )
            }
            return fetchTrendingTopics()
        },
        enabled,
        staleTime: 5 * 60 * 1000,
        retry: false,
        // Errors are silent — the chip row simply won't render when data is unavailable
    })

    return { ...query, isEnabled: enabled }
}
